'use client';

import Image from 'next/image';
import { Suspense, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

import { PUBLIC_PRODUCT_LINKS, PUBLIC_STORE_DEFAULTS } from '@/config/publicProductLinks';
import { useLanguage } from '@/contexts/LanguageContext';
import { getDeviceSetupContent } from '@/i18n/deviceSetupContent';

const discoverySites = ['Indeed', 'LinkedIn', 'Dice', 'Glassdoor', 'MyJobMag', 'Jobberman', 'SEEK', 'Handshake'];

function OnboardingContent() {
  const { locale } = useLanguage();
  const copy = getDeviceSetupContent(locale).smartApplyOnboarding;
  const params = useSearchParams();
  const extensionId = useMemo(() => String(params.get('extensionId') || '').trim(), [params]);
  const validExtensionId = /^[a-p]{32}$/.test(extensionId);
  const connectUrl = validExtensionId ? `/smart-apply/connect?extensionId=${encodeURIComponent(extensionId)}` : '';
  const listingPending = PUBLIC_PRODUCT_LINKS.smartApplyChrome === PUBLIC_STORE_DEFAULTS.chrome;

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#ede9fe,_transparent_36%),linear-gradient(145deg,#f8fafc,#eef2ff)] px-4 py-5 text-slate-900">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl border border-violet-100 bg-white/95 shadow-[0_20px_60px_rgba(76,29,149,0.12)]">
        <section className="bg-gradient-to-br from-violet-700 via-indigo-700 to-slate-900 px-6 py-6 text-white sm:px-8">
          <div className="flex items-center gap-3">
            <Image src="/image/JobolarAiLogoV2.png" alt="JOBolar.Ai" width={40} height={40} className="h-10 w-10 rounded-lg bg-white object-contain p-1" />
            <span className="text-lg font-extrabold tracking-tight">{copy.brand}</span>
          </div>
          <h1 className="mt-4 max-w-3xl text-2xl font-black leading-tight sm:text-3xl">{copy.title}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-5 text-indigo-100">{copy.intro}</p>
        </section>

        <section className="px-6 py-6 sm:px-8">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {copy.tools.map((tool) => (
              <article key={tool.title} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-violet-100 font-black text-violet-700">{tool.icon}</span><h2 className="font-extrabold">{tool.title}</h2></div>
                <p className="mt-2 text-sm leading-5 text-slate-600">{tool.text}</p>
              </article>
            ))}
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[1.3fr_.7fr]">
            <section className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <h2 className="font-extrabold text-indigo-950">{copy.coverageTitle}</h2>
              <p className="mt-1 text-sm leading-5 text-indigo-900/75">{copy.coverageBody}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">{discoverySites.map((site) => <span key={site} className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-indigo-700 shadow-sm">{site}</span>)}</div>
            </section>
            <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <h2 className="font-extrabold text-amber-950">{copy.beforeTitle}</h2>
              <p className="mt-1 text-sm leading-5 text-amber-900/80">{copy.beforeBody}</p>
            </section>
          </div>

          <div className="mt-5 text-center">
            {validExtensionId ? (
              <a href={connectUrl} className="inline-flex rounded-xl bg-gradient-to-r from-violet-700 to-indigo-700 px-7 py-3 text-sm font-extrabold text-white shadow-lg shadow-indigo-200">{copy.connect}</a>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">{copy.openFromExtension}</div>
            )}
            <div className="mt-3">
              <a href={PUBLIC_PRODUCT_LINKS.smartApplyChrome} target="_blank" rel="noreferrer" className="inline-flex rounded-xl border border-violet-200 bg-white px-5 py-2.5 text-sm font-extrabold text-violet-800 transition hover:bg-violet-50">
                {listingPending ? copy.listingPending : copy.install}
              </a>
            </div>
            <p className="mt-3 text-xs text-slate-500">{copy.passwordSafety}</p>
          </div>
        </section>
      </div>
    </main>
  );
}

function OnboardingFallback() {
  const { locale } = useLanguage();
  return <main className="min-h-screen bg-slate-50 p-10 text-center">{getDeviceSetupContent(locale).smartApplyOnboarding.loading}</main>;
}

export default function SmartApplyOnboardingPage() {
  return <Suspense fallback={<OnboardingFallback />}><OnboardingContent /></Suspense>;
}
